import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { CustomerMunicipality } from '../../municipalities/municipality-service';
import { CustomerType } from '../../customers/customer-service';
import { includes, isEmpty, sortBy } from 'lodash';

@autoinject
export class MunicipalitySelector {

	municipalityCodeForPartialMembers = 'partial_members';
	municipalityCodeForExternalMunicipalities = 'External';

	@bindable availableMunicipalities: CustomerMunicipality[];	
	@bindable preSelectedMunicipalities: string[];
	@bindable selectionChanged: (selectedItems: any) => void;
	@bindable customerType: CustomerType;
	@bindable enabled: boolean;

	@computedFrom('availableMunicipalities')
	get fullMembers(): CustomerMunicipality[] {
		return this.availableMunicipalities.filter(cm => cm.type !== 'PartialMember');
	}
	get partialMembers(): CustomerMunicipality[] {
		return this.availableMunicipalities.filter(cm => cm.type === 'PartialMember');
	}

	selectedItems: { [code: string]: boolean } = {}; // code is municipality code / "Total" / "PartialMembers"

	get showMunicipalities(): boolean {
		return this.availableMunicipalities.length > 1;
	}

	resolveMunicipalityCode(municipality: CustomerMunicipality): string {
		return municipality.code 
			? municipality.code.toString()
			: this.municipalityCodeForExternalMunicipalities;
	}

	attached() {
		this.recalculateSelectedItems();
	}

	availableMunicipalitiesChanged() {
		this.recalculateSelectedItems();
	}

	preSelectedMunicipalitiesChanged() {
		this.recalculateSelectedItems();
	}

	customerTypeChanged() {
		this.recalculateSelectedItems();
	}

	enabledChanged() {
		this.selectedItemsChanged();
	}

	recalculateSelectedItems() {

		this.selectedItems = {};

		this.selectedItems['Total'] = !isEmpty(this.preSelectedMunicipalities)
			? includes(this.preSelectedMunicipalities, null)
			: true;
		
		this.selectedItems[this.municipalityCodeForPartialMembers] = !isEmpty(this.partialMembers)
			? (!isEmpty(this.preSelectedMunicipalities)
				? includes(this.preSelectedMunicipalities, this.municipalityCodeForPartialMembers) // something already selected -> honor that
				: this.partialMembers.length > 1) // check by default if partial members exist. If just 1 partial member exists, the whole checkbox for sum is not shown.
			: false; // no partial members exist

		this.availableMunicipalities && this.availableMunicipalities.forEach(m => {
			const code = this.resolveMunicipalityCode(m);
			this.selectedItems[code] = this.preSelectedMunicipalities && this.preSelectedMunicipalities.length
				? includes(this.preSelectedMunicipalities, code)
				: this.customerType === 'Other' && m.type !== 'PartialMember'
		}); 
	}

	selectedItemsChanged() {
		if (!this.selectionChanged) {
			return;
		}

		let selectedMunicipalityCodes = [];

		if (this.availableMunicipalities.length > 1) {
			selectedMunicipalityCodes = Object.keys(this.selectedItems).filter(key => this.selectedItems[key] === true);
		} else {
			selectedMunicipalityCodes.push(this.availableMunicipalities[0]);
		}

		const orderedSelection = this.orderSelected(selectedMunicipalityCodes);
		if(orderedSelection[0] === 'Total') orderedSelection[0] = null;

		this.selectionChanged({selectedItems: orderedSelection});
	}

	orderSelected(selection: string[]) {
		
		const availableMunicipalityCodesInOrder =
			this.availableMunicipalities.map(mun => this.resolveMunicipalityCode(mun));

		let codesWithIndexes = selection.map(code => ({ code: code, index: availableMunicipalityCodesInOrder.indexOf(code) }));
		
		const firstPartialMemberIndex = this.availableMunicipalities.findIndex(cm => cm.type === 'PartialMember');

		const partialMembersSum = codesWithIndexes.find(x => x.code === this.municipalityCodeForPartialMembers);
		if (partialMembersSum) {
			codesWithIndexes.filter(x => x.index >= firstPartialMemberIndex).forEach(x => x.index = x.index + 1);
			partialMembersSum.index = firstPartialMemberIndex;
		}
		
		return sortBy(codesWithIndexes, x => x.index).map(x => x.code);
	}
}
